/**
 * NotificationBell - Bell icon with badge and dropdown
 *
 * Features:
 * - Badge showing unread count (hidden when 0, shows "9+" when >9)
 * - Dropdown with recent notifications
 * - Mark all as read action
 * - Link to full notifications page
 * - Two variants: icon-only (header) and sidebar (full-width with label)
 */

import { ROUTES } from '@smarthome/ui';
import { Bell, Check, Loader2 } from 'lucide-react';
import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { useNotifications } from '../context/NotificationContext';

import { NotificationItem } from './NotificationItem';

import { cn } from '@/components/ui/utils';

interface NotificationBellProps {
  /** Display variant: icon (compact for header) or sidebar (full-width with label) */
  variant?: 'icon' | 'sidebar';
}

interface MenuPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  openUpward: boolean;
}

export function NotificationBell({ variant = 'icon' }: NotificationBellProps) {
  const navigate = useNavigate();
  const {
    unreadCount,
    recentNotifications,
    isLoadingNotifications,
    markAsRead,
    markAllAsRead,
    isMarkingAsRead,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Calculate position when opening
  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const menuHeight = 400; // Approximate max height of dropdown
    const menuWidth = 320; // w-80 = 320px

    // Determine if we should open upward
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;

    if (variant === 'sidebar') {
      // Sidebar: open to the right of the button, aligned with top or bottom
      const left = rect.right + 8;
      // Make sure it doesn't go off screen horizontally
      const adjustedLeft = Math.min(left, viewportWidth - menuWidth - 8);

      if (openUpward) {
        setMenuPosition({
          bottom: viewportHeight - rect.bottom,
          left: adjustedLeft,
          openUpward: true,
        });
      } else {
        setMenuPosition({
          top: rect.top,
          left: adjustedLeft,
          openUpward: false,
        });
      }
    } else {
      // Header icon: open below or above, aligned to right edge
      if (openUpward) {
        setMenuPosition({
          bottom: viewportHeight - rect.top + 8,
          right: viewportWidth - rect.right,
          openUpward: true,
        });
      } else {
        setMenuPosition({
          top: rect.bottom + 8,
          right: viewportWidth - rect.right,
          openUpward: false,
        });
      }
    }
  }, [variant]);

  // Recalculate on resize
  useLayoutEffect(() => {
    if (isOpen) {
      calculatePosition();
    }
  }, [isOpen, calculatePosition]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    function handleResize() {
      if (isOpen) calculatePosition();
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [isOpen, calculatePosition]);

  const handleToggle = () => {
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = (notification: {
    id?: string;
    readAt?: string | null;
    context?: { [key: string]: unknown };
  }) => {
    // Mark as read
    if (notification.id && !notification.readAt) {
      markAsRead(notification.id);
    }

    // Navigate to deep link if present
    const context = notification.context;
    const deepLink = context ? context['deepLink'] : undefined;
    if (deepLink && typeof deepLink === 'string') {
      if (deepLink.startsWith('/')) {
        navigate(deepLink);
      } else if (deepLink.startsWith('http')) {
        window.open(deepLink, '_blank');
      }
    }

    setIsOpen(false);
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate(ROUTES.NOTIFICATIONS);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  // Format badge text
  const badgeText = unreadCount > 9 ? '9+' : unreadCount.toString();

  // Render the trigger button based on variant
  const renderTrigger = () => {
    if (variant === 'sidebar') {
      return (
        <button
          ref={buttonRef}
          type="button"
          onClick={handleToggle}
          className={cn(
            'mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] font-medium',
            'text-gray-500 transition-all duration-150',
            'hover:bg-gray-100 hover:text-gray-900',
            'dark:text-zinc-400 dark:hover:bg-white/[0.04] dark:hover:text-zinc-200',
            isOpen && 'bg-gray-100 text-gray-900 dark:bg-white/[0.04] dark:text-zinc-200'
          )}
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <div className="relative">
            <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
            {unreadCount > 0 && (
              <span
                className={cn(
                  'absolute -right-1.5 -top-1.5 flex items-center justify-center',
                  'min-w-[16px] rounded-full bg-blue-600 px-1',
                  'text-[9px] font-semibold text-white'
                )}
                aria-hidden="true"
              >
                {badgeText}
              </span>
            )}
          </div>
          <span>Notifications</span>
        </button>
      );
    }

    // Icon variant (default)
    return (
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className={cn(
          'relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
          'text-gray-500 hover:bg-gray-100 hover:text-gray-900',
          'dark:text-zinc-400 dark:hover:bg-white/[0.06] dark:hover:text-white',
          isOpen && 'bg-gray-100 text-gray-900 dark:bg-white/[0.06] dark:text-white'
        )}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Bell className="h-5 w-5" strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span
            className={cn(
              'absolute -right-0.5 -top-0.5 flex items-center justify-center',
              'min-w-[18px] rounded-full bg-blue-600 px-1 py-0.5',
              'text-[10px] font-semibold text-white',
              'ring-2 ring-white dark:ring-[#18181b]'
            )}
            aria-hidden="true"
          >
            {badgeText}
          </span>
        )}
      </button>
    );
  };

  // Build menu style from position
  const menuStyle: React.CSSProperties = {
    position: 'fixed',
  };
  if (menuPosition) {
    if (menuPosition.top !== undefined) menuStyle.top = menuPosition.top;
    if (menuPosition.bottom !== undefined) menuStyle.bottom = menuPosition.bottom;
    if (menuPosition.left !== undefined) menuStyle.left = menuPosition.left;
    if (menuPosition.right !== undefined) menuStyle.right = menuPosition.right;
  }

  return (
    <>
      {renderTrigger()}

      {/* Dropdown menu */}
      {isOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className={cn(
              'z-[9999] w-80 rounded-lg border border-gray-200 bg-white shadow-xl',
              'dark:border-white/[0.06] dark:bg-[#18181b]',
              'animate-in fade-in-0 zoom-in-95 duration-150'
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-white/[0.06]">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllAsRead}
                  disabled={isMarkingAsRead}
                  className={cn(
                    'flex items-center gap-1.5 text-xs font-medium',
                    'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300',
                    'disabled:cursor-not-allowed disabled:opacity-50'
                  )}
                >
                  {isMarkingAsRead ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Check className="h-3 w-3" />
                  )}
                  Mark all as read
                </button>
              )}
            </div>

            {/* Notification list */}
            <div className="max-h-80 overflow-y-auto">
              {isLoadingNotifications ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : recentNotifications.length === 0 ? (
                <div className="py-8 text-center">
                  <Bell className="mx-auto h-8 w-8 text-gray-300 dark:text-zinc-600" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-zinc-400">
                    No notifications yet
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-white/[0.04]">
                  {recentNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onClick={() => handleNotificationClick(notification)}
                      compact
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 dark:border-white/[0.06]">
              <button
                type="button"
                onClick={handleViewAll}
                className={cn(
                  'w-full px-4 py-3 text-center text-sm font-medium',
                  'text-blue-600 hover:bg-gray-50 dark:text-blue-400 dark:hover:bg-white/[0.02]',
                  'transition-colors'
                )}
              >
                View all notifications
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
